import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { t as uiText } from '../../i18n';
import i18n from '../../i18n';
import { functions } from '../../lib/firebase';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import { useWorkspaceEntitlements } from '../../features/plans/useWorkspaceEntitlements';
import { useIsWorkspaceAdminQuery } from '../../features/auth/queries/workspaceRoleQueries';
import { formatFileSize } from '../../features/workspace/fileStatsUtils';
import UpgradeModal from '../UpgradeModal';
import { NcgLaunchOfferNotice } from './NcgLaunchOfferNotice';
import { isNcgSeries } from '../../features/plans/series';
import MemberListModal from '../MemberListModal';
import './BillingAccessNotices.css';

const BANNER_VAR = '--billing-banner-h';
const OPEN_FILES_EVENT = 'notreu-open-files';
const OPEN_TAB_KEY = 'notreu.openTab';

type OverageKey = 'members' | 'storage' | 'publicRag' | 'internalRag';

type OverageItem = {
  key: OverageKey;
  current: number;
  limit: number;
};

function formatCredits(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatGraceDate(date: Date): string {
  const language = i18n.resolvedLanguage || i18n.language;
  return date.toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function overageLabel(item: OverageItem): string {
  if (item.key === 'members') {
    return uiText('Members: {{current}} / {{limit}}', { current: item.current, limit: item.limit });
  }
  if (item.key === 'storage') {
    return uiText('Storage: {{current}} / {{limit}}', {
      current: formatFileSize(item.current),
      limit: formatFileSize(item.limit),
    });
  }
  if (item.key === 'publicRag') {
    return uiText('Public AI credits this month: {{current}} / {{limit}}', {
      current: formatCredits(item.current),
      limit: formatCredits(item.limit),
    });
  }
  return uiText('Workspace AI credits this month: {{current}} / {{limit}}', {
    current: formatCredits(item.current),
    limit: formatCredits(item.limit),
  });
}

const BillingAccessNotices: React.FC = () => {
  const { selectedWorkspace } = useWorkspaceContext();
  const { effective, loading: entitlementsLoading } = useWorkspaceEntitlements();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const workspaceId = selectedWorkspace?.id ?? '';
  const { data: isAdmin = false, isFetched: isAdminFetched } = useIsWorkspaceAdminQuery(
    workspaceId,
    !!workspaceId
  );

  const bannerRef = React.useRef<HTMLDivElement>(null);
  const billingStatus = selectedWorkspace?.billingStatus;
  const inGrace = billingStatus === 'past_due' || billingStatus === 'unpaid';
  const graceUntil = selectedWorkspace?.graceUntil;

  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [items, setItems] = useState<OverageItem[]>([]);
  const [overageReload, setOverageReload] = useState(0);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const dismissKey = `${workspaceId}:${pathname}`;
  const showOverage =
    items.length > 0 && dismissedKey !== dismissKey && !membersOpen && !upgradeOpen;

  useEffect(() => {
    const root = document.documentElement;
    if (!inGrace || !bannerRef.current) {
      root.style.setProperty(BANNER_VAR, '0px');
      return;
    }
    const el = bannerRef.current;
    const apply = () => root.style.setProperty(BANNER_VAR, `${el.offsetHeight}px`);
    apply();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    observer?.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', apply);
      root.style.setProperty(BANNER_VAR, '0px');
    };
  }, [inGrace, graceUntil, isAdminFetched, portalError]);

  useEffect(() => {
    if (!workspaceId || entitlementsLoading || effective?.planId !== 'free') {
      setItems([]);
      return;
    }
    let cancelled = false;
    const fn = httpsCallable<{ workspaceId: string }, { items: OverageItem[] }>(
      functions,
      'getBillingOverageFn'
    );
    fn({ workspaceId })
      .then((res) => {
        if (!cancelled) setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      })
      .catch((error) => {
        console.error('[BillingAccessNotices] overage load failed', error);
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, entitlementsLoading, effective?.planId, overageReload]);

  const openPortal = async () => {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      const mode = import.meta.env.MODE === 'production' ? 'live' : 'test';
      const fn = httpsCallable(functions, 'createBillingPortalSessionFn');
      const res = await fn({
        returnUrl: window.location.href,
        mode,
      });
      const url = (res.data as { url?: string })?.url;
      if (!url) throw new Error('missing url');
      window.location.assign(url);
    } catch (error) {
      console.error('[BillingAccessNotices] portal failed', error);
      setPortalError(uiText('Could not open the payment page. Please try again later.'));
      setOpeningPortal(false);
    }
  };

  const openFiles = () => {
    if (!workspaceId) return;
    const root = `/${workspaceId}`;
    sessionStorage.setItem(OPEN_TAB_KEY, 'file');
    setDismissedKey(`${workspaceId}:${root}`);
    if (pathname === root) {
      window.dispatchEvent(new Event(OPEN_FILES_EVENT));
      return;
    }
    navigate(root);
  };

  return (
    <>
      {inGrace && (
        <div ref={bannerRef} className="billing-banner" role="status">
          <div>
            <strong>{uiText('Payment failed. Update the card to keep this plan.')}</strong>
            {graceUntil && (
              <div>{uiText('The payment deadline is {{date}}.', { date: formatGraceDate(graceUntil) })}</div>
            )}
            {portalError && <div>{portalError}</div>}
          </div>
          {isAdminFetched && isAdmin && (
            <button type="button" onClick={() => void openPortal()} disabled={openingPortal}>
              {uiText('Update payment')}
            </button>
          )}
          {isAdminFetched && !isAdmin && (
            <span>{uiText('Ask a workspace administrator to update the payment method.')}</span>
          )}
        </div>
      )}

      {showOverage && (
        <div className="billing-overage" role="dialog" aria-modal="true">
          <div className="billing-overage-card">
            <h2>{uiText('This workspace is over the Free limits')}</h2>
            <p>
              {uiText(
                'Existing data is kept. You cannot add more until usage is within the Free limits, or you change the plan.'
              )}
            </p>
            <ul>
              {items.map((item) => (
                <li key={item.key}>{overageLabel(item)}</li>
              ))}
            </ul>
            {isNcgSeries(selectedWorkspace?.seriesId) && <NcgLaunchOfferNotice part="resubscribe" />}
            <div className="billing-overage-actions">
              {isAdminFetched && isAdmin && items.some((item) => item.key === 'members') && (
                <button type="button" onClick={() => setMembersOpen(true)}>
                  {uiText('Manage members')}
                </button>
              )}
              {items.some((item) => item.key === 'storage') && (
                <button type="button" onClick={openFiles}>
                  {uiText('Manage files')}
                </button>
              )}
              {isAdminFetched && isAdmin && (
                <button type="button" className="primary" onClick={() => setUpgradeOpen(true)}>
                  {uiText('Change plan')}
                </button>
              )}
              {!isAdmin && isAdminFetched && (
                <span>{uiText('Please ask a workspace administrator to change the plan.')}</span>
              )}
              <button type="button" onClick={() => setDismissedKey(dismissKey)}>
                {uiText('Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {membersOpen && (
        <MemberListModal
          onClose={() => {
            setMembersOpen(false);
            setOverageReload((n) => n + 1);
          }}
        />
      )}
      <UpgradeModal isOpen={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
};

export default BillingAccessNotices;
